import { z } from 'zod';

// =============================================================================
// Draft Schema
// =============================================================================

const draftSchema = z.object({
	id: z.string(),
	authorId: z.string(),
	status: z.enum(['active', 'inactive']),
	updatedAt: z.number(),
	type: z.enum(['user_draft', 'ai_draft']).nullish(),
});

// =============================================================================
// Pre-drafting input
// =============================================================================

/**
 * Initial user input from the petition creation flow
 */
const userInputSchemaBase = z.object({
	intro: z.enum(['ai', 'user']),
	issuePrompt: z.string(),
	personalStory: z.string(),
	flow: z.enum(['chat']),
});

const userInputSchemaDraft = userInputSchemaBase.extend({
	intro: userInputSchemaBase.shape.intro.nullish(),
	issuePrompt: userInputSchemaBase.shape.issuePrompt.nullish(),
	personalStory: userInputSchemaBase.shape.personalStory.nullish(),
	flow: userInputSchemaBase.shape.flow.nullish(),
});

// =============================================================================
// AI-generated Content Schemas
// =============================================================================

const aiGeneratedContentSchemaBase = z.object({
	ask: z.string(),
	description: z.string(),
	image: z.object({ url: z.string(), type: z.enum(['AI_GENERATED']) }),
});

const aiGeneratedContentSchemaDraft = aiGeneratedContentSchemaBase.extend({
	ask: aiGeneratedContentSchemaBase.shape.ask.nullish(),
	description: aiGeneratedContentSchemaBase.shape.description.nullish(),
	image: aiGeneratedContentSchemaBase.shape.image.nullish(),
});

// =============================================================================
// Petition Content Schemas
// =============================================================================

/**
 * Petition content (user-created/modified)
 */
const petitionContentSchemaBase = z.object({
	ask: z.string().min(1),
	description: z.string().min(1),
	image: z.object({ url: z.string().min(1), type: z.enum(['USER_UPLOADED', 'AI_GENERATED']) }),
	reach: z.enum(['local', 'national', 'global']),
	location: z.object({ id: z.string().min(1), name: z.string().min(1) }),
});

const petitionContentSchemaDraft = petitionContentSchemaBase.extend({
	ask: petitionContentSchemaBase.shape.ask.nullish(),
	description: petitionContentSchemaBase.shape.description.nullish(),
	image: petitionContentSchemaBase.shape.image.nullish(),
	reach: petitionContentSchemaBase.shape.reach.nullish(),
	location: petitionContentSchemaBase.shape.location.nullish(),
});

const petitionContentSchemaPublish = petitionContentSchemaBase.extend({
	ask: petitionContentSchemaBase.shape.ask,
	description: petitionContentSchemaBase.shape.description,
	image: petitionContentSchemaBase.shape.image.nullish(),
	reach: petitionContentSchemaBase.shape.reach.nullish(),
	location: petitionContentSchemaBase.shape.location.nullish(),
});

// =============================================================================
// Editing Draft Schemas
// =============================================================================

/**
 * In-progress edits buffered while the user is on an edit step. Committed back
 * into `petitionDraft` only on validated submission, so partial/invalid input
 * can't pollute the canonical draft via blur or non-validating navigation.
 */
const editingDraftSchemaBase = z.object({
	ask: z.string(),
	description: z.string(),
});

const editingDraftSchemaDraft = editingDraftSchemaBase.extend({
	ask: editingDraftSchemaBase.shape.ask.nullish(),
	description: editingDraftSchemaBase.shape.description.nullish(),
});

// =============================================================================
// Metadata Schemas
// =============================================================================

/**
 * User context information
 */
const userContextSchema = z.object({
	id: z.string().optional(),
	loginOrJoinMethod: z.string().optional(),
	initialLoginState: z.enum(['AUTHENTICATED', 'IDENTIFIED', 'GUEST']).optional(),
});

/**
 * Common metadata across all draft types
 */
const draftMetadataSchemaBase = z.object({
	draftMode: z.string(),
	imageJobId: z.string(),
	imageFetchCount: z.number(),
	imageGenerationCount: z.number(),
	user: userContextSchema,
	variant: z.enum(['control', 'fake_control', 'preview_edit_variant_a', 'preview_edit_variant_b', 'chat_agent']),
	deviceId: z.string(),
	angleToken: z.string(),
});

const draftMetadataSchemaDraft = draftMetadataSchemaBase.extend({
	draftMode: draftMetadataSchemaBase.shape.draftMode.nullish(),
	imageJobId: draftMetadataSchemaBase.shape.imageJobId.nullish(),
	imageFetchCount: draftMetadataSchemaBase.shape.imageFetchCount.nullish(),
	imageGenerationCount: draftMetadataSchemaBase.shape.imageGenerationCount.nullish(),
	user: draftMetadataSchemaBase.shape.user.nullish(),
	variant: draftMetadataSchemaBase.shape.variant.nullish(),
	deviceId: draftMetadataSchemaBase.shape.deviceId.nullish(),
	angleToken: draftMetadataSchemaBase.shape.angleToken.nullish(),
});

// =============================================================================
// Draft Schema
// =============================================================================

export const petitionDraftSchema = draftSchema.extend({
	input: userInputSchemaDraft.nullish(),
	recommendedDraft: aiGeneratedContentSchemaDraft.nullish(),
	petitionDraft: petitionContentSchemaDraft.nullish(),
	editingDraft: editingDraftSchemaDraft.nullish(),
	metadata: draftMetadataSchemaDraft.nullish(),
});

// =============================================================================
// Publish Schema
// =============================================================================

export const publishPetitionSchema = z.object({
	recommendedDraft: aiGeneratedContentSchemaDraft.nullish(),
	petitionDraft: petitionContentSchemaPublish,
});

// =============================================================================
// Post-draft Schemas - validates whether enough petition content is present
// to start mid-flow (preview/create steps)
// =============================================================================

export const postDraftUserSchema = z.object({
	type: draftSchema.shape.type,
	petitionDraft: petitionContentSchemaBase.pick({ ask: true, description: true }),
});

export const postDraftAiSchema = z.object({
	type: draftSchema.shape.type,
	petitionDraft: petitionContentSchemaBase.pick({ ask: true, description: true }),
	recommendedDraft: aiGeneratedContentSchemaBase.pick({ ask: true, description: true }),
});

// =============================================================================
// Type Exports
// =============================================================================

export type PetitionDraftSchema = z.infer<typeof petitionDraftSchema>;
export type PublishPetitionSchema = z.infer<typeof publishPetitionSchema>;
